/** In-memory client cache with TTL + in-flight request deduplication. */

const cache = new Map();
const inflight = new Map();
const subscribers = new Map();

export const queryKeys = {
  problems: 'problems',
  contests: 'contests',
  topSolvers: 'users/top-solvers',
  leaderboard: 'users/leaderboard',
  notifications: 'notifications',
  programs: 'problems/my-programs',
  problem: (slug, admin = false) => `problem:${admin ? 'admin:' : ''}${slug}`,
};

export const STALE_TIMES = {
  problems: 5 * 60 * 1000,
  contests: 2 * 60 * 1000,
  leaderboard: 2 * 60 * 1000,
  topSolvers: 2 * 60 * 1000,
  problem: 10 * 60 * 1000,
  notifications: 45 * 1000,
  programs: 5 * 60 * 1000,
};

const notify = (key) => {
  const set = subscribers.get(key);
  if (!set) return;
  set.forEach((cb) => cb());
};

export const getCacheEntry = (key) => cache.get(key) ?? null;

export const getQueryData = (key) => getCacheEntry(key)?.data ?? null;

export const isStale = (entry, staleTime) => {
  if (!entry) return true;
  return Date.now() - entry.fetchedAt > staleTime;
};

export const subscribe = (key, callback) => {
  if (!subscribers.has(key)) subscribers.set(key, new Set());
  subscribers.get(key).add(callback);
  return () => subscribers.get(key)?.delete(callback);
};

export const setQueryData = (key, data) => {
  cache.set(key, { data, fetchedAt: Date.now() });
  notify(key);
};

export const invalidateQuery = (key) => {
  cache.delete(key);
  inflight.delete(key);
  notify(key);
};

export const invalidateQueries = (prefix) => {
  for (const key of cache.keys()) {
    if (key === prefix || key.startsWith(`${prefix}:`)) {
      cache.delete(key);
      inflight.delete(key);
    }
  }
  notify(prefix);
};

/**
 * Fetch with deduplication. Returns cached data when fresh unless `force`.
 * Serves stale data immediately while revalidating in the background when `revalidate`.
 */
export const fetchQuery = async (
  key,
  fetcher,
  { staleTime = 60_000, force = false, revalidate = true } = {},
) => {
  const entry = getCacheEntry(key);

  if (!force && entry && !isStale(entry, staleTime)) {
    return entry.data;
  }

  if (!force && entry && revalidate) {
    if (!inflight.has(key)) {
      const promise = fetcher()
        .then((data) => {
          cache.set(key, { data, fetchedAt: Date.now() });
          inflight.delete(key);
          notify(key);
          return data;
        })
        .catch((err) => {
          inflight.delete(key);
          throw err;
        });
      inflight.set(key, promise);
    }
    return entry.data;
  }

  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, fetchedAt: Date.now() });
      inflight.delete(key);
      notify(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
};
